import { CheckCircle2, HelpCircle, Shield, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { IdentityScoreResult } from '@/features/identities/identity-score'
import { CircularProgress } from './CircularProgress'

export interface IdentityScoreCardProps {
  scoreResult: IdentityScoreResult
  scoreLabel: string
  /** Score color set — `bg`/`text`/`ring` token classes for the active band. */
  colors: { bg: string; text: string; ring: string }
}

export function IdentityScoreCard({ scoreResult, scoreLabel, colors }: IdentityScoreCardProps) {
  const { t } = useTranslation()

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg text-foreground">
              {t('identity.detail.score.title')}
            </CardTitle>
          </div>
          {/* Help Icon with Tooltip */}
          <div className="relative group">
            <button className="p-1 rounded-full hover:bg-muted transition-colors">
              <HelpCircle className="h-5 w-5 text-muted-foreground hover:text-foreground transition-colors" />
            </button>
            <div className="absolute right-0 top-full mt-2 w-72 p-3 bg-popover border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <h4 className="font-semibold mb-2 text-xs text-popover-foreground">
                {t('identity.detail.score.criteria')}
              </h4>
              <ul className="space-y-1 text-xs text-muted-foreground">
                <li>+1 {t('identity.detail.score.fullName')}</li>
                <li>+1 {t('identity.detail.score.contact')}</li>
                <li>+1 {t('identity.detail.score.addressOnRecord')}</li>
                <li>+1 {t('identity.detail.score.verifiedDoc')}</li>
                <li>+1 {t('identity.detail.score.multipleVerified')}</li>
              </ul>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* The gauge sits above the breakdown at 375 px, beside it from sm up. */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
          {/* Circular Progress - Smaller */}
          <div className="flex flex-col items-center gap-1">
            <CircularProgress score={scoreResult.score} maxScore={scoreResult.maxScore} />
            <Badge className={`${colors.bg} text-white text-xs`}>{scoreLabel}</Badge>
          </div>

          {/* Compact Score Breakdown */}
          <div className="flex-1 space-y-2">
            {scoreResult.breakdown.map((item, index) => (
              <div
                key={index}
                className={`flex items-center justify-between py-2 px-3 rounded text-sm ${
                  item.achieved ? 'bg-success-surface' : 'bg-muted'
                }`}
              >
                <div className="flex items-center gap-2">
                  {item.achieved ? (
                    <CheckCircle2 className="h-4 w-4 text-success" />
                  ) : (
                    <XCircle className="h-4 w-4 text-muted-foreground/50" />
                  )}
                  <span className={item.achieved ? 'text-foreground' : 'text-muted-foreground'}>
                    {item.label}
                  </span>
                </div>
                <span
                  className={`font-mono ${item.achieved ? 'text-success' : 'text-muted-foreground/50'}`}
                >
                  +{item.points}
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
